import React, { useState, useEffect } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { getRecaptchaToken, isRecaptchaEnabled } from '../lib/recaptcha';
import { 
  formatPhoneNumber, 
  formatName,
  validateEmail, 
  validateName,
  getPhoneDigits 
} from '../lib/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCapturePopup = ({ delay = 5000 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    agreeToEmails: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const openedAtRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) openedAtRef.current = Date.now();
  }, [isOpen]);

  useEffect(() => {
    // Check if popup was already shown/dismissed
    const popupDismissed = localStorage.getItem('emailPopupDismissed');
    const popupSubmitted = localStorage.getItem('emailPopupSubmitted');
    
    if (popupDismissed || popupSubmitted) return;

    // Show popup after delay
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('emailPopupDismissed', 'true');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone number
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
      return;
    }
    
    // Format names
    if (name === 'firstName' || name === 'lastName') {
      setFormData(prev => ({ ...prev, [name]: formatName(value) }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!validateName(formData.firstName).isValid || !validateName(formData.lastName).isValid) {
      setError('Please enter valid first and last names');
      return;
    }
    
    if (!validateEmail(formData.email).isValid) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.agreeToEmails) {
      setError('Please agree to receive emails/texts to continue');
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken('subscribe_form');
      await axios.post(`${BACKEND_URL}/api/auth/subscribe`, {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone ? getPhoneDigits(formData.phone) : null,
        website: honeypot,
        recaptcha_token: recaptchaToken,
        form_ms: openedAtRef.current ? Date.now() - openedAtRef.current : null
      });
      
      setSuccess(true);
      localStorage.setItem('emailPopupSubmitted', 'true');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429 || status === 400) {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again in a few minutes.');
        setLoading(false);
        return;
      }
      // Network/server hiccup: show success for better UX (we can retry later)
      setSuccess(true);
      localStorage.setItem('emailPopupSubmitted', 'true');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" data-testid="email-capture-popup">
      <div className="popup-container">
        <button 
          className="popup-close" 
          onClick={handleClose}
          aria-label="Close popup"
          data-testid="popup-close-btn"
        >
          <X size={24} />
        </button>

        {success ? (
          <div className="popup-success">
            <CheckCircle2 size={56} className="success-check" />
            <h3>You're on the list! 🎉</h3>
            <p>Thanks for joining the GoCalFit community, <strong>{formData.firstName}</strong>!</p>
            <div className="popup-spam-notice">
              <p>📧 <strong>Check your inbox!</strong></p>
              <p>If you don't see our email, check your spam/junk folder and move it to your inbox.</p>
              <p>Add <strong>hello@gocalfit.com</strong> to your contacts!</p>
            </div>
            <Button onClick={handleClose} className="popup-done-btn">
              Got it!
            </Button>
          </div>
        ) : (
          <>
            <div className="popup-header">
              <h3>Stay Connected 💫</h3>
              <p>Get exclusive updates, class schedules, and special offers delivered straight to your inbox.</p>
            </div>

            <form onSubmit={handleSubmit} className="popup-form">
              {/* Honeypot — hidden from real users, bots auto-fill it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="popup-form-row">
                <div className="popup-form-group">
                  <Label htmlFor="popup-firstName">First Name *</Label>
                  <Input
                    id="popup-firstName"
                    name="firstName"
                    type="text"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    data-testid="popup-firstname-input"
                  />
                </div>
                <div className="popup-form-group">
                  <Label htmlFor="popup-lastName">Last Name *</Label>
                  <Input
                    id="popup-lastName"
                    name="lastName"
                    type="text"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    data-testid="popup-lastname-input"
                  />
                </div>
              </div>

              <div className="popup-form-group">
                <Label htmlFor="popup-email">Email *</Label>
                <Input
                  id="popup-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="popup-email-input"
                />
              </div>

              <div className="popup-form-group">
                <Label htmlFor="popup-phone">Phone Number (optional)</Label>
                <Input
                  id="popup-phone"
                  name="phone"
                  type="tel"
                  placeholder="(123) 456-7890"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="popup-phone-input"
                />
              </div>

              <div className="popup-checkbox">
                <Checkbox
                  id="popup-agreeToEmails"
                  checked={formData.agreeToEmails}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, agreeToEmails: checked }))
                  }
                  data-testid="popup-consent-checkbox"
                />
                <Label htmlFor="popup-agreeToEmails" className="popup-checkbox-label">
                  Yes, I want to receive emails/texts from GoCalFit about classes, events, and special offers. *
                </Label>
              </div>

              {error && <div className="popup-error">{error}</div>}

              <Button 
                type="submit" 
                className="popup-submit-btn"
                disabled={loading}
                data-testid="popup-submit-btn"
              >
                {loading ? 'Joining...' : 'Join the Community'}
              </Button>

              <p className="popup-privacy">
                We respect your privacy. Unsubscribe anytime.
                {isRecaptchaEnabled() && (
                  <> Protected by reCAPTCHA — Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a> & <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a> apply.</>
                )}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailCapturePopup;
